/**
 * Measurements quiz "Find my sizes" — Figma Quizzes › `Measurements quiz · desktop`
 * 3233:18690 / `mobile` 3234:19645.
 *
 * Welcome → Gender (only if Personal data has none, R120) → Basic measurements
 * (women 7, men 6) → More measurements → Additional (women 3, men 4) → Done.
 * Every measurement step can be skipped; values go to Sizes as they are entered (R109).
 */
import type {
  FairProfileV2Body,
  FairProfileV2MeasureField,
  FairProfileV2MeasureKey,
} from '@/widgets/profile/fair-v2/fair-profile-v2-measure.data'

export type SizesQuizStep =
  | { kind: 'welcome' }
  | { kind: 'gender' }
  | { kind: 'field'; key: FairProfileV2MeasureKey }
  | { kind: 'more' }
  | { kind: 'done' }

export type SizesQuizStepId = string

export function sizesQuizStepId(step: SizesQuizStep): SizesQuizStepId {
  return step.kind === 'field' ? `field:${step.key}` : step.kind
}

export interface SizesQuizFlowInput {
  askGender: boolean
  fields: FairProfileV2MeasureField[]
  /** "Add N more" was chosen on More measurements. */
  withAdditional: boolean
}

export function sizesQuizSteps({ askGender, fields, withAdditional }: SizesQuizFlowInput): SizesQuizStep[] {
  const basic = fields.filter((field) => field.group === 'basic')
  const additional = fields.filter((field) => field.group === 'additional')
  const steps: SizesQuizStep[] = [{ kind: 'welcome' }]
  if (askGender) steps.push({ kind: 'gender' })
  steps.push(...basic.map((field) => ({ kind: 'field' as const, key: field.key })))
  if (additional.length) {
    steps.push({ kind: 'more' })
    if (withAdditional) steps.push(...additional.map((field) => ({ kind: 'field' as const, key: field.key })))
  }
  steps.push({ kind: 'done' })
  return steps
}

/** 0…1 over everything between Welcome and Done. */
export function sizesQuizProgress(steps: SizesQuizStep[], id: SizesQuizStepId): number {
  const index = steps.findIndex((step) => sizesQuizStepId(step) === id)
  if (index <= 0) return 0
  return Math.min(1, index / (steps.length - 1))
}

/** Foot length moves in 0.5 cm (09 note); the rest in whole units. */
export function sizesQuizRulerStep(key: FairProfileV2MeasureKey, imperial: boolean): number {
  if (key === 'footLength' || imperial) return 0.5
  return 1
}

/** Height uses the vertical ruler (mobile note: "vertical for Height"). */
export function sizesQuizRulerOrientation(key: FairProfileV2MeasureKey): 'horizontal' | 'vertical' {
  return key === 'height' ? 'vertical' : 'horizontal'
}

/** More measurements shows this many house chips (10 note). */
export const SIZES_QUIZ_MORE_BRANDS_LIMIT = 5

/**
 * The houses named on More measurements: those the size recommender serves
 * (`GET /size-recommendation/brands`), in its order, that have a logo in the brand catalogue
 * (10 note: only brands with a logo), at most `limit`.
 */
export function sizesQuizMoreBrands(
  served: readonly string[],
  catalogue: readonly { name: string; logo?: string | null }[],
  limit = SIZES_QUIZ_MORE_BRANDS_LIMIT,
): Array<{ name: string; logo: string }> {
  const byName = new Map(catalogue.map((brand) => [brand.name.toLowerCase(), brand]))
  const out: Array<{ name: string; logo: string }> = []
  for (const name of served) {
    const brand = byName.get(name.toLowerCase())
    if (brand?.logo) out.push({ name: brand.name, logo: brand.logo })
    if (out.length >= limit) break
  }
  return out
}

export function sizesQuizBodyGender(body: FairProfileV2Body): 'woman' | 'man' {
  return body === 'feminine' ? 'woman' : 'man'
}
